<template>
  <v-app style="overflow-y: hidden; max-height: 100%">
    <main-nav
      v-if="!isReaderPage"
      :open-cart="() => {
        showDrawer = !showDrawer;
      }"
    />
    <auth-loading-overlay :show="$auth.loading" />
    <error-snackbar
      v-model="errorSnackbar"
      :error-label="$t('serverError')"
      :error-message="errorMessage"
      :debug-message="debugMessage"
    />
    <general-snackbar />
    <v-main style="overflow-y: hidden;">
      <!-- Desktop: side drawer -->
      <v-navigation-drawer
        v-if="!isMobileCart"
        v-model="showDrawer"
        right
        temporary
        fixed
        data-testid="cart-drawer"
        style="min-width: 30%; width: 300px; border-radius: 10px 0px 0px 10px;"
      >
        <Cart
          v-if="showDrawer"
          :close-cart="() => {
            showDrawer = false;
          }"
        />
      </v-navigation-drawer>

      <!-- Mobile: bottom sheet -->
      <div
        v-if="isMobileCart"
        :class="['cart-sheet-overlay', { 'cart-sheet-overlay--open': showDrawer }]"
        @click.self="showDrawer = false"
      >
        <transition name="cart-sheet">
          <div
            v-if="showDrawer"
            class="cart-sheet"
            data-testid="cart-sheet"
          >
            <button
              type="button"
              class="cart-sheet__handle"
              aria-label="Close cart"
              @click="showDrawer = false"
            >
              <span class="cart-sheet__bar" />
            </button>
            <Cart
              :close-cart="() => {
                showDrawer = false;
              }"
            />
          </div>
        </transition>
      </div>
      <v-container
        class="pa-0 main-container"
        style="max-height: 100%; overflow-y: auto"
      >
        <v-breadcrumbs
          v-if="breadCrumbs && !isReaderPage"
          class="bread-crumbs no-print"
          :items="breadCrumbs"
          divider=">"
          large
        />
        <router-view />
      </v-container>
    </v-main>
    <ekitabu-footer v-if="!isReaderPage" />
    <MobileNav v-if="!isReaderPage" />
    <claimed-order-modal />
  </v-app>
</template>

<script>
import MainNav from "@/components/navigation/MainNav";
import ErrorSnackbar from "@/components/ErrorSnackbar";
import GeneralSnackbar from "@/components/GeneralSnackbar";
import { mapGetters, mapMutations, mapActions, mapState } from "vuex";
import AuthLoadingOverlay from "@/components/AuthLoadingOverlay";
import EkitabuFooter from "@/components/footer/EkitabuFooter";
import GetUserRegionMixin from "@/mixins/GetUserRegionMixin";
import seoMixin from "@/mixins/seoMixin";
import ClaimedOrderModal from "@/components/ClaimedOrderModal";
import MobileNav from "@/components/navigation/MobileNav.vue";
import Cart from "./views/checkout/Cart/Cart.vue";

export default {
  name: "App",
  components: {
    MainNav,
    ErrorSnackbar,
    GeneralSnackbar,
    AuthLoadingOverlay,
    EkitabuFooter,
    ClaimedOrderModal,
    MobileNav,
    Cart,
  },
  mixins: [GetUserRegionMixin, seoMixin],
  data() {
    return {
      errorSnackbar: false,
      showDrawer: false,
    };
  },
  computed: {
    ...mapGetters("serverError", ["errorCode", "errorMessage", "debugMessage"]),
    ...mapGetters("siteSettings", ["showMobileNav"]),
    ...mapGetters("siteFunctionality", ["showCartDrawer"]),
    breadCrumbs() {
      if (typeof this.$route.meta.breadCrumbs === "function") {
        return this.$route.meta.breadCrumbs.call(this, this.$route);
      }
      return this.$route.meta.breadCrumbs;
    },
    isReaderPage() {
      return this.$route.name === 'reader';
    },
    isMobileCart() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  watch: {
    errorSnackbar(newValue, oldValue) {
      if (newValue == false && oldValue == true) {
        setTimeout(() => {
          this.setErrorCode("");
          this.setErrorMessage("");
          this.setDebugMessage("");
        }, 300);
      }
    },
    errorMessage(newErrorMessage) {
      if (newErrorMessage) {
        this.errorSnackbar = true;
      }
    },
    errorCode(newErrorCode) {
      if (newErrorCode) {
        this.errorSnackbar = true;
      }
    },
    showCartDrawer(newValue) {
      if (newValue != this.showDrawer) {
        this.showDrawer = newValue;
      }
    },
    showDrawer(newValue) {
      if (newValue != this.showCartDrawer) {
        this.changeShowCartDrawer(newValue);
      }
      if (this.isMobileCart) {
        const lock = newValue ? 'hidden' : '';
        document.documentElement.style.overflow = lock;
        document.body.style.overflow = lock;
        const main = document.querySelector('.main-container');
        if (main) main.style.overflow = lock;
      }
    },
    $route() {
      if (this.isMobileCart && this.showDrawer) {
        this.showDrawer = false;
      }
    },
  },
  mounted() {},
  methods: {
    ...mapMutations("serverError", ["setErrorCode", "setErrorMessage", "setDebugMessage"]),
    ...mapActions('siteFunctionality', ['changeShowCartDrawer']),
    ...mapActions('snackbar', ['hideSnackbar'])
  },
};
</script>

<style scoped lang="scss">
@import "~vuetify/src/styles/styles.sass";

.main-container.container {
  max-width: $max-content-width;
  height: 100%;
}

.bread-crumbs {
  height: 30px;
}

::v-deep .v-breadcrumbs.bread-crumbs .v-breadcrumbs__item,
::v-deep .v-breadcrumbs.bread-crumbs .v-breadcrumbs__divider {
  color: var(--v-primaryText-base);
}

::v-deep .v-breadcrumbs.bread-crumbs .v-breadcrumbs__item:hover {
  text-decoration: underline;
}

::v-deep .v-breadcrumbs.bread-crumbs > li:last-of-type > .v-breadcrumbs__item {
  color: var(--v-primary-base);
}

/* ── Mobile cart bottom sheet ── */
.cart-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: $z-overlay;
  background: rgba(0, 0, 0, 0);
  pointer-events: none;
  transition: background 0.3s ease;
}

.cart-sheet-overlay--open {
  background: rgba(0, 0, 0, 0.45);
  pointer-events: auto;
}

.cart-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: $z-sheet;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
}

::v-deep .cart-sheet .cart-root {
  max-height: none !important;
  height: auto !important;
  overflow: visible !important;
}

::v-deep .cart-sheet .cart-items-section {
  overflow: visible !important;
  max-height: none !important;
}

.cart-sheet__handle {
  display: flex;
  justify-content: center;
  padding: 12px 0 4px;
  cursor: pointer;
  flex-shrink: 0;
  background: none;
  border: none;
  width: 100%;
}

.cart-sheet__bar {
  width: 36px;
  height: 4px;
  border-radius: 4px;
  background: $book-grey-light;
}

/* Transition */
.cart-sheet-enter-active {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.cart-sheet-leave-active {
  transition: transform 0.25s ease-in;
}

.cart-sheet-enter,
.cart-sheet-leave-to {
  transform: translateY(100%);
}
</style>
